import { c as _c } from "react/compiler-runtime";
import { createContext, ReactNode, useState } from 'react';
import useSearchQuery from '../hooks/useSearchQuery';
type ColumnWidth = number | {
  max?: number;
  min?: number;
  type?: 'percentage' | 'px';
};
type CustomField = {
  element: JSX.Element;
  clickable_area: boolean;
};
type Field = string | number | boolean | JSX.Element | CustomField | null;
interface Row {
  id: string | number;
  selected?: boolean;
  selectable?: boolean;
  highlighted?: boolean;
  outdated?: boolean;
  fields?: Field[];
  widths?: ColumnWidth[];
  element?: React.ReactNode;
  has_error?: boolean;
  expendable?: boolean;
}
interface NestedTable {
  headers: Header[];
  rows: Row[];
}
type Header = string | JSX.Element;

// DynamicColumn is a string like "body.property-name" or "headers.property-name"
type DynamicColumn = string;

// Helper to parse a dynamic column string into its components (e.g. "body.sku" or "data.items:200")
interface ParsedDynamicColumn {
  column_string: string;
  root: string; // first path segment: "body", "query", "headers", "data", "metadata", etc.
  path: (string | number)[];
  display_name: string;
  width?: number;
}
const parseDynamicColumn = (column_string: string): ParsedDynamicColumn => {
  // Check if column_string contains a width specification (format: "field_path:width")
  let field_path = column_string;
  let width: number | undefined;
  const width_separator_index = column_string.lastIndexOf(':');
  if (width_separator_index !== -1) {
    const potential_width = column_string.substring(width_separator_index + 1);
    const parsed_width = parseInt(potential_width, 10);
    if (!isNaN(parsed_width) && parsed_width > 0) {
      width = parsed_width;
      field_path = column_string.substring(0, width_separator_index);
    }
  }
  const parts = field_path.split('.');
  const root = parts[0];
  const path: (string | number)[] = parts.slice(1);
  return {
    column_string,
    root,
    path,
    display_name: field_path,
    width
  };
};
interface TableContextType {
  top_offset: number;
  headers: Header[];
  widths: ColumnWidth[];
  rows: (Row | NestedTable)[];
  dynamic_columns: DynamicColumn[];
  setTopOffset: (offset: number) => void;
  setHeaders: (headers: Header[]) => void;
  setWidths: (widths: ColumnWidth[]) => void;
  setRows: (rows: (Row | NestedTable)[]) => void;
  updateTableConfig: (config: Partial<TableConfig>) => void;
  addDynamicColumn: (column: DynamicColumn) => void;
  removeDynamicColumn: (column: DynamicColumn) => void;
  isDynamicColumnActive: (column: DynamicColumn) => boolean;
  reorderDynamicColumns: (active_index: number, over_index: number) => void;
  updateDynamicColumnWidth: (field_path: string, new_width: number) => void;
  has_scrolled: boolean;
}
interface TableConfig {
  top_offset: number;
  headers: Header[];
  widths: ColumnWidth[];
  rows: (Row | NestedTable)[];
  dynamic_columns?: DynamicColumn[];
}
const defaultContext: TableContextType = {
  top_offset: 0,
  headers: [],
  widths: [],
  rows: [],
  dynamic_columns: [],
  setTopOffset: () => undefined,
  setHeaders: () => undefined,
  setWidths: () => undefined,
  setRows: () => undefined,
  updateTableConfig: () => undefined,
  addDynamicColumn: () => undefined,
  removeDynamicColumn: () => undefined,
  isDynamicColumnActive: () => false,
  reorderDynamicColumns: () => undefined,
  updateDynamicColumnWidth: () => undefined,
  has_scrolled: false
};
export const TableContext = createContext<TableContextType>(defaultContext);
interface TableProviderProps {
  children: ReactNode;
  initialConfig?: Partial<TableConfig>;
  has_scrolled: boolean;
}
export const TableProvider: React.FC<TableProviderProps> = t0 => {
  const $ = _c(43);
  const {
    children,
    initialConfig,
    has_scrolled
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const [top_offset, setTopOffset] = useState(initialConfig?.top_offset || 0);
  let t1;
  if ($[0] !== initialConfig?.headers) {
    t1 = initialConfig?.headers || [];
    $[0] = initialConfig?.headers;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [headers, setHeaders] = useState(t1);
  let t2;
  if ($[2] !== initialConfig?.widths) {
    t2 = initialConfig?.widths || [];
    $[2] = initialConfig?.widths;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [widths, setWidths] = useState(t2);
  let t3;
  if ($[4] !== initialConfig?.rows) {
    t3 = initialConfig?.rows || [];
    $[4] = initialConfig?.rows;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [rows, setRows] = useState(t3);
  let t4;
  bb0: {
    if ("dynamic_columns" in query) {
      if (query.dynamic_columns === null) {
        let t5;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = [];
          $[6] = t5;
        } else {
          t5 = $[6];
        }
        t4 = t5;
        break bb0;
      }
      if (Array.isArray(query.dynamic_columns)) {
        t4 = query.dynamic_columns as DynamicColumn[];
        break bb0;
      }
      if (typeof query.dynamic_columns === "string") {
        let t5;
        if ($[7] !== query.dynamic_columns) {
          t5 = [query.dynamic_columns];
          $[7] = query.dynamic_columns;
          $[8] = t5;
        } else {
          t5 = $[8];
        }
        t4 = t5 as DynamicColumn[];
        break bb0;
      }
    }
    let t5;
    if ($[9] !== initialConfig?.dynamic_columns) {
      t5 = initialConfig?.dynamic_columns || [];
      $[9] = initialConfig?.dynamic_columns;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = t5;
  }
  const dynamic_columns = t4;
  let t5;
  if ($[11] !== updateSearchQuery) {
    t5 = config => {
      if (config.top_offset !== undefined) {
        setTopOffset(config.top_offset);
      }
      if (config.headers !== undefined) {
        setHeaders(config.headers);
      }
      if (config.widths !== undefined) {
        setWidths(config.widths);
      }
      if (config.rows !== undefined) {
        setRows(config.rows);
      }
      if (config.dynamic_columns !== undefined) {
        updateSearchQuery({
          dynamic_columns: config.dynamic_columns.length === 0 ? null : config.dynamic_columns
        });
      }
    };
    $[11] = updateSearchQuery;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const updateTableConfig = t5;
  let t6;
  if ($[13] !== dynamic_columns || $[14] !== updateSearchQuery) {
    t6 = (active_index, over_index) => {
      const new_columns = [...dynamic_columns];
      const [removed] = new_columns.splice(active_index, 1);
      new_columns.splice(over_index, 0, removed);
      updateSearchQuery({
        dynamic_columns: new_columns
      });
    };
    $[13] = dynamic_columns;
    $[14] = updateSearchQuery;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const reorderDynamicColumns = t6;
  let t7;
  if ($[16] !== dynamic_columns || $[17] !== updateSearchQuery) {
    t7 = column => {
      const target_field_path = column.split(":")[0];
      const already_exists = dynamic_columns.some(col => {
        const col_field_path = col.split(":")[0];
        return col_field_path === target_field_path;
      });
      if (!already_exists) {
        const new_columns_0 = [...dynamic_columns, column];
        updateSearchQuery({
          dynamic_columns: new_columns_0
        });
      }
    };
    $[16] = dynamic_columns;
    $[17] = updateSearchQuery;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const addDynamicColumn = t7;
  let t8;
  if ($[19] !== dynamic_columns || $[20] !== updateSearchQuery) {
    t8 = column_0 => {
      const target_field_path_0 = column_0.split(":")[0];
      const new_columns_1 = dynamic_columns.filter(col_0 => {
        const col_field_path_0 = col_0.split(":")[0];
        return col_field_path_0 !== target_field_path_0;
      });
      updateSearchQuery({
        dynamic_columns: new_columns_1.length === 0 ? null : new_columns_1
      });
    };
    $[19] = dynamic_columns;
    $[20] = updateSearchQuery;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const removeDynamicColumn = t8;
  let t9;
  if ($[22] !== dynamic_columns || $[23] !== updateSearchQuery) {
    t9 = (field_path, new_width) => {
      const new_columns_2 = dynamic_columns.map(col_1 => {
        const col_field_path_1 = col_1.split(":")[0];
        if (col_field_path_1 === field_path) {
          return `${field_path}:${Math.round(new_width)}`;
        }
        return col_1;
      });
      updateSearchQuery({
        dynamic_columns: new_columns_2
      });
    };
    $[22] = dynamic_columns;
    $[23] = updateSearchQuery;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const updateDynamicColumnWidth = t9;
  let t10;
  if ($[25] !== dynamic_columns) {
    t10 = column_1 => {
      const target_field_path_1 = column_1.split(":")[0];
      return dynamic_columns.some(col_2 => {
        const col_field_path_2 = col_2.split(":")[0];
        return col_field_path_2 === target_field_path_1;
      });
    };
    $[25] = dynamic_columns;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const isDynamicColumnActive = t10;
  let t11;
  if ($[27] !== addDynamicColumn || $[28] !== dynamic_columns || $[29] !== has_scrolled || $[30] !== headers || $[31] !== isDynamicColumnActive || $[32] !== removeDynamicColumn || $[33] !== reorderDynamicColumns || $[34] !== rows || $[35] !== top_offset || $[36] !== updateDynamicColumnWidth || $[37] !== updateTableConfig || $[38] !== widths) {
    t11 = {
      top_offset,
      headers,
      widths,
      rows,
      dynamic_columns,
      setTopOffset,
      setHeaders,
      setWidths,
      setRows,
      updateTableConfig,
      addDynamicColumn,
      removeDynamicColumn,
      isDynamicColumnActive,
      reorderDynamicColumns,
      updateDynamicColumnWidth,
      has_scrolled
    };
    $[27] = addDynamicColumn;
    $[28] = dynamic_columns;
    $[29] = has_scrolled;
    $[30] = headers;
    $[31] = isDynamicColumnActive;
    $[32] = removeDynamicColumn;
    $[33] = reorderDynamicColumns;
    $[34] = rows;
    $[35] = top_offset;
    $[36] = updateDynamicColumnWidth;
    $[37] = updateTableConfig;
    $[38] = widths;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== children || $[41] !== t11) {
    t12 = <TableContext.Provider value={t11}>{children}</TableContext.Provider>;
    $[40] = children;
    $[41] = t11;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
};
export type { ColumnWidth, Field, Row, NestedTable, Header, TableConfig, DynamicColumn, ParsedDynamicColumn };
export { parseDynamicColumn };